import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Copy, Trash2, Pencil, Eye } from 'lucide-react';
import { API, StatusBadge, fmtDate, cardCls, txtMain, txtSub } from './nlShared';
import { LoadingSpinner } from '../core';
import { toast } from 'sonner';

const FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'draft', label: 'Brouillons' },
  { id: 'scheduled', label: 'Programmées' },
  { id: 'sent', label: 'Envoyées' },
];
const SORTS = [
  { id: 'date', label: 'Date' },
  { id: 'perf', label: 'Performances' },
  { id: 'alpha', label: 'A → Z' },
];

export function NlCampaigns({ theme, onOpen, onEdit }) {
  const light = theme === 'light';
  const [camps, setCamps] = useState(null);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('date');

  const load = () => axios.get(`${API}/admin/nl/campaigns`, { withCredentials: true }).then(r => setCamps(r.data)).catch(() => setCamps([]));
  useEffect(() => { load(); }, []);

  if (!camps) return <LoadingSpinner />;

  let list = camps.filter(c => (filter === 'all' || c.status === filter || (filter === 'sent' && c.status === 'sending')));
  if (q.trim()) {
    const s = q.toLowerCase();
    list = list.filter(c => (c.name || '').toLowerCase().includes(s) || (c.subject || '').toLowerCase().includes(s) || (c.category || '').toLowerCase().includes(s));
  }
  list = [...list].sort((a, b) => {
    if (sort === 'perf') return (b.open_rate || 0) - (a.open_rate || 0);
    if (sort === 'alpha') return (a.name || '').localeCompare(b.name || '');
    return (b.updated_at || '').localeCompare(a.updated_at || '');
  });

  const duplicate = async (id) => {
    const { data } = await axios.post(`${API}/admin/nl/campaigns/${id}/duplicate`, {}, { withCredentials: true });
    toast.success('Campagne dupliquée');
    load();
    onEdit(data.id);
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer cette campagne ?')) return;
    await axios.delete(`${API}/admin/nl/campaigns/${id}`, { withCredentials: true });
    toast.success('Campagne supprimée');
    load();
  };

  const th = `text-left text-[11px] uppercase tracking-wide font-semibold px-3 py-2.5 ${txtSub(light)}`;
  const td = `px-3 py-3 text-sm ${light ? 'text-gray-700' : 'text-white/80'}`;

  return (
    <div className="space-y-4" data-testid="nl-campaigns">
      <div className="flex items-center gap-3 flex-wrap">
        <div className={`flex rounded-lg p-0.5 ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
          {FILTERS.map(f => (
            <button key={f.id} data-testid={`nl-filter-${f.id}`} onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${filter === f.id ? 'bg-[#E9641A] text-white' : light ? 'text-gray-500' : 'text-white/40 hover:text-white'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg flex-1 min-w-[200px] max-w-sm ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
          <Search size={14} className={txtSub(light)} />
          <input data-testid="nl-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher une campagne…"
            className={`bg-transparent text-sm outline-none flex-1 ${txtMain(light)} placeholder:opacity-40`} />
        </div>
        <select data-testid="nl-sort" value={sort} onChange={e => setSort(e.target.value)}
          className={`text-xs font-semibold rounded-lg px-3 py-2 outline-none ${light ? 'bg-gray-100 text-gray-700' : 'bg-white/5 text-white/70'}`}>
          {SORTS.map(s => <option key={s.id} value={s.id}>Tri : {s.label}</option>)}
        </select>
      </div>

      <div className={`${cardCls(light)} overflow-hidden`}>
        <table className="w-full">
          <thead><tr className={light ? 'border-b border-gray-200' : 'border-b border-white/10'}>
            <th className={th}>Campagne</th><th className={th}>Catégorie</th><th className={`${th} hidden lg:table-cell`}>Auteur</th>
            <th className={th}>Date</th><th className={th}>Statut</th><th className={`${th} hidden md:table-cell`}>Ouv. / Clics</th><th className={th}></th>
          </tr></thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={7} className={`${td} text-center py-10 opacity-50`}>Aucune campagne trouvée</td></tr>
            )}
            {list.map(c => (
              <tr key={c.id} data-testid={`nl-row-${c.id}`} className={`${light ? 'border-b border-gray-100 hover:bg-gray-50' : 'border-b border-white/5 hover:bg-white/[0.03]'} transition-colors`}>
                <td className={td}>
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-8 rounded overflow-hidden flex-none ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
                      {c.thumb && <img src={c.thumb} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0">
                      <p className={`font-semibold truncate max-w-[220px] ${txtMain(light)}`}>{c.name}</p>
                      <p className={`text-xs truncate max-w-[220px] ${txtSub(light)}`}>{c.subject || 'Sans objet'}</p>
                    </div>
                  </div>
                </td>
                <td className={td}><span className={`text-xs px-2 py-0.5 rounded-full ${light ? 'bg-gray-100' : 'bg-white/8'}`}>{c.category}</span></td>
                <td className={`${td} hidden lg:table-cell text-xs`}>{c.author || '—'}</td>
                <td className={`${td} text-xs whitespace-nowrap`}>{fmtDate(c.status === 'scheduled' ? c.scheduled_at : (c.sent_at || c.updated_at))}</td>
                <td className={td}><StatusBadge status={c.status} /></td>
                <td className={`${td} hidden md:table-cell text-xs whitespace-nowrap`}>{c.status === 'sent' ? `${c.open_rate}% / ${c.ctr}%` : '—'}</td>
                <td className={`${td} whitespace-nowrap`}>
                  <div className="flex items-center gap-1 justify-end">
                    <button title="Voir" data-testid={`nl-view-${c.id}`} onClick={() => onOpen(c.id)} className={`p-1.5 rounded-md ${light ? 'hover:bg-gray-100 text-gray-400' : 'hover:bg-white/10 text-white/40'} hover:!text-[#E9641A]`}><Eye size={15} /></button>
                    {c.status !== 'sent' && c.status !== 'sending' && (
                      <button title="Modifier" data-testid={`nl-edit-${c.id}`} onClick={() => onEdit(c.id)} className={`p-1.5 rounded-md ${light ? 'hover:bg-gray-100 text-gray-400' : 'hover:bg-white/10 text-white/40'} hover:!text-[#E9641A]`}><Pencil size={15} /></button>
                    )}
                    <button title="Dupliquer" data-testid={`nl-dup-${c.id}`} onClick={() => duplicate(c.id)} className={`p-1.5 rounded-md ${light ? 'hover:bg-gray-100 text-gray-400' : 'hover:bg-white/10 text-white/40'} hover:!text-[#E9641A]`}><Copy size={15} /></button>
                    <button title="Supprimer" data-testid={`nl-del-${c.id}`} onClick={() => remove(c.id)} className={`p-1.5 rounded-md ${light ? 'hover:bg-gray-100 text-gray-400' : 'hover:bg-white/10 text-white/40'} hover:!text-red-500`}><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
